/**
 * Spatial indexing utilities for finding groups of overlapping markers.
 *
 * Wraps rbush to provide connected-component grouping: markers whose
 * bounding boxes overlap (or are within `spacing` of each other) are
 * collected into the same group.
 */

import RBush from 'rbush';
import { markerBounds } from './geometry.js';

/**
 * Build an rbush spatial index from an array of marker descriptors.
 *
 * Each marker descriptor must have: { id, x, y, width, height, anchorX, anchorY }
 *
 * @param {{ id: string|number, x: number, y: number, width: number, height: number, anchorX: number, anchorY: number }[]} markers
 * @returns {RBush}
 */
export function buildIndex(markers) {
  const tree = new RBush();
  const items = markers.map((m) => {
    const bounds = markerBounds(m);
    return {
      ...bounds,
      marker: m,
    };
  });
  tree.load(items);
  return tree;
}

/**
 * Expand a bounding box by a given amount on all sides.
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} bbox
 * @param {number} amount
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number }}
 */
function expandBBox(bbox, amount) {
  return {
    minX: bbox.minX - amount,
    minY: bbox.minY - amount,
    maxX: bbox.maxX + amount,
    maxY: bbox.maxY + amount,
  };
}

/**
 * Find groups of markers whose bounding boxes overlap or are within
 * `spacing` pixels of each other. Uses union-find on the spatial index
 * for efficient connected-component detection.
 *
 * @param {{ id: string|number, x: number, y: number, width: number, height: number, anchorX: number, anchorY: number }[]} markers
 * @param {number} [spacing=0]
 * @returns {Array<Array<{ id: string|number, x: number, y: number, width: number, height: number, anchorX: number, anchorY: number }>>}
 *   Array of groups. Each group has 2+ markers. Isolated markers are not returned.
 */
export function findOverlapGroups(markers, spacing = 0) {
  if (markers.length <= 1) {
    return [];
  }

  const tree = buildIndex(markers);

  // Union-Find data structure
  const parent = new Map();
  const rank = new Map();

  function find(id) {
    if (parent.get(id) !== id) {
      parent.set(id, find(parent.get(id)));
    }
    return parent.get(id);
  }

  function union(a, b) {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return;
    const rankA = rank.get(ra) || 0;
    const rankB = rank.get(rb) || 0;
    if (rankA < rankB) {
      parent.set(ra, rb);
    } else if (rankA > rankB) {
      parent.set(rb, ra);
    } else {
      parent.set(rb, ra);
      rank.set(ra, rankA + 1);
    }
  }

  // Initialize union-find
  for (const m of markers) {
    parent.set(m.id, m.id);
    rank.set(m.id, 0);
  }

  // For each marker, search the tree for neighbors and union them
  for (const m of markers) {
    const bounds = markerBounds(m);
    const searchBox = expandBBox(bounds, spacing);
    const neighbors = tree.search(searchBox);
    for (const neighbor of neighbors) {
      if (neighbor.marker.id !== m.id) {
        union(m.id, neighbor.marker.id);
      }
    }
  }

  // Collect groups
  const groups = new Map();
  for (const m of markers) {
    const root = find(m.id);
    if (!groups.has(root)) {
      groups.set(root, []);
    }
    groups.get(root).push(m);
  }

  // Return only groups with 2+ markers
  return Array.from(groups.values()).filter((g) => g.length >= 2);
}
